import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiArrowRight, FiStar, FiUsers, FiShield, FiSearch } from 'react-icons/fi';

const Home = () => {
  const { user } = useAuth();
  const dashLink = user?.role === 'admin' ? '/admin' : user?.role === 'tutor' ? '/tutor' : '/student';

  const stats = [
    { value: '500+', label: 'Active Tutors' },
    { value: '1200+', label: 'Students' },
    { value: '98%', label: 'Satisfaction' },
    { value: '50+', label: 'Subjects' },
  ];

  const features = [
    { icon: '🔍', title: 'Smart Search', desc: 'Find tutors and students by subject, location, class, and fee range with powerful filters.' },
    { icon: '🔒', title: 'Verified Profiles', desc: 'Every profile is reviewed and approved by admin before going live on the platform.' },
    { icon: '💳', title: 'Secure Payments', desc: 'Pay ₹50 to unlock contact details. Supports UPI, PhonePe, GPay, Paytm and cards.' },
    { icon: '⚡', title: 'Instant Connect', desc: 'Once unlocked, get direct contact details and connect immediately.' },
  ];

  const subjects = ['Mathematics', 'Physics', 'Chemistry', 'Biology', 'English', 'Computer Science', 'Hindi', 'Social Studies'];

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 50%, #312E81 100%)', color: 'white', padding: '80px 0 100px', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative blobs */}
        <div style={{ position: 'absolute', top: '-100px', right: '-100px', width: '400px', height: '400px', borderRadius: '50%', background: 'rgba(108,99,255,0.15)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(245,158,11,0.1)', filter: 'blur(60px)' }} />

        <div className="container" style={{ position: 'relative', zIndex: 1 }}>
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(108,99,255,0.2)', border: '1px solid rgba(108,99,255,0.4)', borderRadius: '20px', padding: '6px 16px', marginBottom: '24px' }}>
                <FiStar size={14} color="#F59E0B" />
                <span style={{ fontSize: '13px', color: '#C7D2FE' }}>India's fastest growing tuition platform</span>
              </div>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(36px, 5vw, 56px)', fontWeight: 800, lineHeight: 1.15, marginBottom: '20px' }}>
                Find the Perfect<br />
                <span style={{ background: 'linear-gradient(135deg, #6C63FF, #F59E0B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Tutor or Student</span>
                <br />Near You
              </h1>
              <p style={{ fontSize: '17px', color: '#94A3B8', lineHeight: 1.7, marginBottom: '36px', maxWidth: '500px' }}>
                Connect with qualified tutors and motivated students in your area. Pay only to unlock the contact details you need.
              </p>
              <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap' }}>
                {user ? (
                  <Link to={dashLink}><button className="btn-primary-custom" style={{ padding: '14px 32px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>Go to Dashboard <FiArrowRight /></button></Link>
                ) : (
                  <>
                    <Link to="/register?role=tutor"><button className="btn-primary-custom" style={{ padding: '14px 28px', fontSize: '15px' }}>I'm a Tutor 👨‍🏫</button></Link>
                    <Link to="/register?role=student"><button className="btn-outline-custom" style={{ padding: '14px 28px', fontSize: '15px', border: '2px solid rgba(255,255,255,0.3)', color: 'white', background: 'rgba(255,255,255,0.05)' }}>I'm a Student 📚</button></Link>
                  </>
                )}
              </div>
            </div>
            <div className="col-lg-5 mt-5 mt-lg-0">
              {/* Stats card */}
              <div style={{ background: 'rgba(255,255,255,0.05)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '24px', padding: '32px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {stats.map(s => (
                    <div key={s.label} style={{ textAlign: 'center', padding: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '16px' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontSize: '28px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>{s.value}</div>
                      <div style={{ fontSize: '12px', color: '#94A3B8', fontWeight: 500 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subjects strip */}
      <section style={{ background: 'var(--primary)', padding: '16px 0', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', padding: '0 20px' }}>
          {subjects.map(s => (
            <span key={s} style={{ padding: '6px 16px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', fontSize: '13px', color: 'white', fontWeight: 500 }}>{s}</span>
          ))}
        </div>
      </section>

      {/* Features */}
      <section style={{ padding: '80px 0', background: 'white' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, marginBottom: '12px' }}>Why TuitionConnect?</h2>
            <p style={{ color: 'var(--gray)', fontSize: '16px', maxWidth: '500px', margin: '0 auto' }}>Everything you need to find the right tutor or student, all in one place.</p>
          </div>
          <div className="row g-4">
            {features.map(f => (
              <div key={f.title} className="col-sm-6 col-lg-3">
                <div className="card-hover" style={{ background: 'var(--lighter)', borderRadius: '20px', padding: '28px 24px', height: '100%' }}>
                  <div style={{ fontSize: '36px', marginBottom: '16px' }}>{f.icon}</div>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '17px', fontWeight: 700, marginBottom: '8px' }}>{f.title}</h4>
                  <p style={{ color: 'var(--gray)', fontSize: '14px', lineHeight: 1.6, marginBottom: 0 }}>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={{ padding: '80px 0', background: 'var(--lighter)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '56px' }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, marginBottom: '12px' }}>How It Works</h2>
          </div>
          <div className="row g-4 justify-content-center">
            {[
              { step: '01', title: 'Register', desc: 'Create your profile as a tutor or student. Get verified by admin.' },
              { step: '02', title: 'Search', desc: 'Browse and filter tutors or students by subject, location and more.' },
              { step: '03', title: 'Unlock', desc: 'Pay ₹50 to unlock contact details of the person you want to connect with.' },
              { step: '04', title: 'Connect', desc: 'Get their phone number and reach out directly to start learning!' },
            ].map((s, i) => (
              <div key={i} className="col-sm-6 col-lg-3">
                <div style={{ textAlign: 'center', padding: '28px 16px' }}>
                  <div style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: '18px', color: 'white' }}>{s.step}</div>
                  <h4 style={{ fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 700, marginBottom: '8px' }}>{s.title}</h4>
                  <p style={{ color: 'var(--gray)', fontSize: '14px', lineHeight: 1.6 }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 0', background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 800, color: 'white', marginBottom: '16px' }}>Ready to Get Started?</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '16px', marginBottom: '36px' }}>Join thousands of tutors and students already on TuitionConnect.</p>
          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register?role=tutor"><button style={{ padding: '14px 32px', background: 'white', color: 'var(--primary)', border: 'none', borderRadius: 'var(--radius)', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Register as Tutor</button></Link>
            <Link to="/register?role=student"><button style={{ padding: '14px 32px', background: 'rgba(255,255,255,0.15)', color: 'white', border: '2px solid rgba(255,255,255,0.4)', borderRadius: 'var(--radius)', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Register as Student</button></Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: 'var(--dark)', color: '#94A3B8', padding: '40px 0', textAlign: 'center' }}>
        <div className="container">
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: 700, color: 'white', marginBottom: '8px' }}>Tuition<span style={{ color: 'var(--primary)' }}>Connect</span></div>
          <p style={{ fontSize: '13px', marginBottom: '0' }}>© 2024 TuitionConnect. Connecting tutors & students across India.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
